import React, { useEffect, useMemo, useState } from "react";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  FileTextOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, Tabs, message as antdMessage } from "antd";
import type { MenuProps } from "antd";

import DailyRecapBriefingSummary, {
  getDailyRecapProfileLabel,
} from "./DailyRecapBriefingSummary";
import { formatDailyRecapDate } from "../utils/dailyRecapDisplay";
import { getDailyRecapReportAction } from "../utils/dailyRecapReportLink";
import {
  DAILY_RECAP_BRIEFING_OPTIONS,
  type DailyRecapBriefing,
  type DailyRecapBriefingChoice,
  type DailyRecapBriefingPreference,
} from "../types/dailyRecapBriefing";
import "./DailyRecapBriefingModal.css";

interface DailyRecapBriefingModalProps {
  open: boolean;
  briefing: DailyRecapBriefing;
  firstName: string;
  preference: DailyRecapBriefingPreference;
  presentationConfirmed: boolean;
  presentationConfirming?: boolean;
  onClose: () => void;
  onNavigate: (warehouseId: number, url: string) => void;
  onPreferenceChange: (
    choice: DailyRecapBriefingChoice
  ) => Promise<DailyRecapBriefingPreference>;
  onRetryPresentation?: () => void;
}

const DailyRecapBriefingModal: React.FC<
  DailyRecapBriefingModalProps
> = ({
  open,
  briefing,
  firstName,
  preference,
  presentationConfirmed,
  presentationConfirming = false,
  onClose,
  onNavigate,
  onPreferenceChange,
  onRetryPresentation,
}) => {
  const [activeNotificationId, setActiveNotificationId] = useState(
    String(briefing.notifications[0]?.id || "")
  );
  const [selectedChoice, setSelectedChoice] =
    useState<DailyRecapBriefingChoice>(preference.choice);
  const [savingPreference, setSavingPreference] = useState(false);

  useEffect(() => {
    setActiveNotificationId(String(briefing.notifications[0]?.id || ""));
  }, [briefing.claim_token, briefing.notifications]);

  useEffect(() => {
    setSelectedChoice(preference.choice);
  }, [preference.choice]);

  const activeNotification = useMemo(
    () =>
      briefing.notifications.find(
        (notification) => String(notification.id) === activeNotificationId
      ) || briefing.notifications[0],
    [activeNotificationId, briefing.notifications]
  );

  const tabItems = useMemo(
    () =>
      briefing.notifications.map((notification) => ({
        key: String(notification.id),
        label: notification.warehouse_name || `Établissement ${notification.warehouse_id}`,
      })),
    [briefing.notifications]
  );

  const updatePreference = async (choice: DailyRecapBriefingChoice) => {
    setSavingPreference(true);
    try {
      const updatedPreference = await onPreferenceChange(choice);
      setSelectedChoice(updatedPreference.choice);
      antdMessage.success("Fréquence du briefing mise à jour");
    } catch (error) {
      antdMessage.error(
        (error as any)?.response?.data?.error ||
          "Impossible de modifier la fréquence du briefing"
      );
    } finally {
      setSavingPreference(false);
    }
  };

  const reportAction = getDailyRecapReportAction(
    {
      ...(activeNotification?.data || {}),
      recap_date:
        activeNotification?.data?.recap_date || briefing.recap_date,
    },
    activeNotification?.warehouse_id
  );
  const reportCount = briefing.notifications.length;
  const displayFirstName = firstName || "";
  const activeData = activeNotification?.data || {};
  const profileLabel = getDailyRecapProfileLabel(activeData);
  const preferenceMenu: MenuProps = {
    items: DAILY_RECAP_BRIEFING_OPTIONS.map((option) => ({
      key: option.value,
      label: option.label,
      icon: option.value === selectedChoice ? <CheckOutlined /> : undefined,
    })),
    onClick: ({ key }) =>
      void updatePreference(key as DailyRecapBriefingChoice),
  };
  const frequencyDisabled =
    !presentationConfirmed || !preference.can_manage || savingPreference;

  return (
    <Modal
      centered
      className="daily-recap-briefing-modal"
      open={open}
      width="90vw"
      style={{ maxWidth: 1728 }}
      keyboard
      maskClosable={false}
      destroyOnClose
      onCancel={onClose}
      title={
        <div className="daily-recap-briefing-modal__title">
          <span className="daily-recap-briefing-modal__heading">
            <span className="daily-recap-briefing-modal__title-icon" aria-hidden="true">
              <BarChartOutlined />
            </span>
            <span className="daily-recap-briefing-modal__heading-copy">
              <strong>
                Bonjour{displayFirstName ? ` ${displayFirstName}` : ""}, voici
                l’essentiel du jour
              </strong>
              <small>
                Bilan du {formatDailyRecapDate(briefing.recap_date)}
                {reportCount > 1 ? ` · ${reportCount} établissements` : ""}
                <span>· Profil actif :</span>
                <span className="daily-recap-briefing-modal__profile">
                  {profileLabel}
                </span>
              </small>
            </span>
          </span>
          <span className="daily-recap-briefing-modal__ready">
            <CheckCircleOutlined aria-hidden="true" /> Bilan prêt
          </span>
        </div>
      }
      footer={
        <div className="daily-recap-briefing-modal__footer">
          <div className="daily-recap-briefing-modal__preference">
            <Dropdown
              menu={preferenceMenu}
              trigger={["click"]}
              disabled={frequencyDisabled}
            >
              <Button
                type="link"
                icon={<SettingOutlined />}
                loading={savingPreference}
                disabled={frequencyDisabled}
              >
                Gérer la fréquence du briefing
              </Button>
            </Dropdown>
            {!presentationConfirmed && !presentationConfirming && onRetryPresentation ? (
              <Button type="link" size="small" onClick={onRetryPresentation}>
                Réessayer l’enregistrement
              </Button>
            ) : null}
          </div>

          <div className="daily-recap-briefing-modal__actions">
            <Button onClick={onClose}>Fermer</Button>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              disabled={!activeNotification}
              onClick={() =>
                activeNotification &&
                onNavigate(activeNotification.warehouse_id, reportAction.url)
              }
            >
              {reportAction.label}
            </Button>
          </div>
        </div>
      }
    >
      {reportCount > 1 ? (
        <Tabs
          className="daily-recap-briefing-modal__tabs"
          activeKey={activeNotificationId}
          onChange={setActiveNotificationId}
          items={tabItems}
        />
      ) : null}

      {activeNotification ? (
        <DailyRecapBriefingSummary
          data={activeData}
          message={activeNotification.message}
          reportUrl={reportAction.url}
          onOpenDestination={(url) =>
            onNavigate(activeNotification.warehouse_id, url)
          }
        />
      ) : null}
    </Modal>
  );
};

export default DailyRecapBriefingModal;
